import { useState, useEffect, useMemo } from 'react';
import { cambios } from '../services/api';
import OnboardingTour from '../components/OnboardingTour';
import { HelpCircle } from 'lucide-react';

// Icons
const CalendarIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
);

const ListIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
    </svg>
);

const PlusIcon = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
);

const SwapIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
    </svg>
);

const CloseIcon = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
);

const ChevronLeft = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
    </svg>
);

const ChevronRight = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
);

// Format date helper
const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T12:00:00');
    return date.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' });
};

const formatDateShort = (dateStr) => {
    const date = new Date(dateStr + 'T12:00:00');
    return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
};

// Onboarding tour steps for Mercadillo
const MERCADILLO_TOUR_STEPS = [
    {
        target: '#tour-mercadillo-header',
        title: '¡Bienvenido al Mercadillo!',
        content: 'Aquí puedes intercambiar días de trabajo con tus compañeros. Es como el grupo de WhatsApp, pero más ordenado.'
    },
    {
        target: '#tour-mercadillo-create',
        title: 'Crear una Solicitud',
        content: 'Pulsa este botón + para pedir un día libre. Tendrás que ofrecer días a cambio.'
    },
    {
        target: '#tour-mercadillo-view-toggle',
        title: 'Dos formas de ver',
        content: 'Puedes ver las ofertas en un calendario o en una lista. Elige la que te resulte más fácil.'
    },
    {
        target: '#tour-mercadillo-filter',
        title: 'Filtrar ofertas',
        content: '"Para mí" solo muestra las que encajan con tus días libres. "Todas" muestra todas las solicitudes.'
    },
    {
        target: '#tour-mercadillo-calendar',
        title: 'Días con ofertas',
        content: 'Los días en naranja tienen solicitudes activas. Pulsa uno para ver los detalles.'
    },
    {
        target: '#tour-mercadillo-header',
        title: '⚠️ Regla importante',
        content: 'Los días de VACACIONES solo se pueden intercambiar por otros días de vacaciones. No puedes cambiar un día de vacaciones por una libranza.'
    }
];

// Calendar component for the exchange system
function CalendarioCambios({ ano, mes, diasOfrecidos, misDiasLibres, onDayClick, onMonthChange }) {
    const diasSemana = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

    const diasDelMes = useMemo(() => {
        const firstDay = new Date(ano, mes - 1, 1);
        const lastDay = new Date(ano, mes, 0);
        const days = [];

        // Get day of week (0=Sunday, we want 0=Monday)
        let startDayOfWeek = firstDay.getDay();
        startDayOfWeek = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1;

        // Empty cells before first day
        for (let i = 0; i < startDayOfWeek; i++) {
            days.push({ empty: true });
        }

        // Days of month
        for (let d = 1; d <= lastDay.getDate(); d++) {
            const dateStr = `${ano}-${String(mes).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
            const oferta = diasOfrecidos?.find(o => o.fecha?.split('T')[0] === dateStr);
            const esLibre = misDiasLibres?.some(f => f?.split('T')[0] === dateStr);

            days.push({
                day: d,
                date: dateStr,
                oferta,
                esLibre,
                numOfertas: oferta?.numOfertas || 0
            });
        }

        return days;
    }, [ano, mes, diasOfrecidos, misDiasLibres]);

    const nombreMes = new Date(ano, mes - 1).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });

    return (
        <div className="bg-[var(--bg-secondary)] rounded-2xl p-4 border border-[var(--glass-border)] shadow-sm">
            {/* Header with month navigation */}
            <div className="flex items-center justify-between mb-4">
                <button
                    onClick={() => onMonthChange(-1)}
                    className="p-2 hover:bg-slate-700/50 rounded-lg transition-colors"
                >
                    <ChevronLeft />
                </button>
                <h3 className="text-lg font-bold text-[var(--text-primary)] capitalize">{nombreMes}</h3>
                <button
                    onClick={() => onMonthChange(1)}
                    className="p-2 hover:bg-[var(--glass-bg)] rounded-lg transition-colors text-[var(--text-primary)]"
                >
                    <ChevronRight />
                </button>
            </div>

            {/* Legend */}
            <div className="flex gap-4 mb-4 text-xs text-[var(--text-muted)]">
                <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                    <span>Días disponibles</span>
                </div>
                <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                    <span>Tus días libres</span>
                </div>
            </div>

            {/* Day headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
                {diasSemana.map(d => (
                    <div key={d} className="text-center text-xs text-[var(--text-secondary)] font-medium py-1">
                        {d}
                    </div>
                ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1">
                {diasDelMes.map((dia, idx) => (
                    <button
                        key={idx}
                        disabled={dia.empty}
                        onClick={() => !dia.empty && onDayClick(dia)}
                        className={`
                                aspect-square rounded-lg flex flex-col items-center justify-center relative transition-all
                                ${dia.empty ? 'invisible' : ''}
                                ${dia.oferta ? 'bg-orange-500/20 border border-orange-500/50 hover:bg-orange-500/30 shadow-lg shadow-orange-500/10' : ''}
                                ${dia.esLibre && !dia.oferta ? 'bg-purple-500/20 border border-purple-500/50' : ''}
                                ${!dia.oferta && !dia.esLibre ? 'bg-[var(--glass-bg)] hover:bg-[var(--glass-border)]' : ''}
                            `}
                    >
                        <span className={`text-sm ${dia.oferta ? 'text-orange-300 font-bold' : dia.esLibre ? 'text-purple-300' : 'text-[var(--text-muted)]'}`}>
                            {dia.day}
                        </span>
                        {dia.numOfertas > 0 && (
                            <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center font-bold">
                                {dia.numOfertas}
                            </span>
                        )}
                    </button>
                ))}
            </div>
        </div>
    );
}

// Solicitud card component
function SolicitudCard({ solicitud, esMia, onResponder, onVerRespuestas, onCancelar }) {
    return (
        <div className={`bg-[var(--bg-secondary)] rounded-xl p-4 border transition-all ${esMia ? 'border-cyan-500/50 shadow-lg shadow-cyan-500/5' : 'border-[var(--glass-border)]'}`}>
            {esMia && (
                <div className="text-xs bg-cyan-500/20 text-cyan-400 px-2 py-1 rounded-full inline-block mb-2">
                    Tu solicitud
                </div>
            )}

            <div className="flex items-start justify-between">
                <div className="flex-1">
                    {!esMia && (
                        <p className="text-[var(--text-primary)] font-bold mb-1">{solicitud.conductor_nombre}</p>
                    )}
                    <div className="text-[var(--text-muted)] mb-2 font-medium text-sm">
                        <span className="text-emerald-500 font-black">NECESITA:</span>{' '}
                        <span className="whitespace-nowrap text-[var(--text-primary)]">{formatDate(solicitud.dia_necesito?.split('T')[0])} ({solicitud.turno_necesito})</span>
                    </div>

                    <div className="flex flex-wrap gap-2">
                        <span className="text-purple-400 text-sm font-semibold">OFRECE:</span>
                        {solicitud.ofertas?.map((o, i) => (
                            <span
                                key={i}
                                className={`px-2 py-1 rounded-lg text-xs ${o.usado
                                    ? 'bg-gray-600/30 text-[var(--text-secondary)] line-through'
                                    : 'bg-purple-500/20 text-purple-300'
                                    }`}
                                title={o.usado ? 'Este día ya fue usado en otro intercambio' : ''}
                            >
                                {formatDateShort(o.dia_ofrezco?.split('T')[0])}
                            </span>
                        ))}
                    </div>
                </div>

                <div className="flex flex-col gap-2 ml-4">
                    {esMia ? (
                        <>
                            {solicitud.num_respuestas > 0 && (
                                <button
                                    onClick={() => onVerRespuestas(solicitud)}
                                    className="bg-cyan-500 hover:bg-cyan-600 text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                                >
                                    {solicitud.num_respuestas} respuestas
                                </button>
                            )}
                            {solicitud.estado === 'cerrada' && (
                                <span className="text-green-400 text-sm">✓ Cerrada</span>
                            )}
                            {solicitud.estado === 'abierta' && onCancelar && (
                                <button
                                    onClick={() => onCancelar(solicitud.id)}
                                    className="bg-red-500/20 hover:bg-red-500/40 text-red-400 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                                >
                                    Cancelar
                                </button>
                            )}
                        </>
                    ) : (
                        <button
                            onClick={() => onResponder(solicitud)}
                            disabled={solicitud.miRespuesta}
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${solicitud.miRespuesta
                                ? 'bg-gray-600 text-[var(--text-muted)] cursor-not-allowed'
                                : 'bg-emerald-500 hover:bg-emerald-600 text-white'
                                }`}
                        >
                            {solicitud.miRespuesta ? 'Ya respondiste' : 'Responder'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

// Inline Calendar Picker component for the modal
function CalendarPicker({ selectedDate, onSelect, diasPermitidos, colorAccent = 'emerald' }) {
    const [viewMes, setViewMes] = useState(1);
    const [viewAno, setViewAno] = useState(2026);
    const diasSemana = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

    const diasPermitidosSet = useMemo(() => new Set(diasPermitidos || []), [diasPermitidos]);

    const diasDelMes = useMemo(() => {
        const firstDay = new Date(viewAno, viewMes - 1, 1);
        const lastDay = new Date(viewAno, viewMes, 0);
        const days = [];

        let startDayOfWeek = firstDay.getDay();
        startDayOfWeek = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1;

        for (let i = 0; i < startDayOfWeek; i++) {
            days.push({ empty: true });
        }

        for (let d = 1; d <= lastDay.getDate(); d++) {
            const dateStr = `${viewAno}-${String(viewMes).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
            days.push({
                day: d,
                date: dateStr,
                permitido: diasPermitidosSet.has(dateStr),
                selected: selectedDate === dateStr
            });
        }
        return days;
    }, [viewAno, viewMes, diasPermitidosSet, selectedDate]);

    const nombreMes = new Date(viewAno, viewMes - 1).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });

    const handleMonthChange = (delta) => {
        let newMes = viewMes + delta;
        let newAno = viewAno;
        if (newMes < 1) { newMes = 12; newAno--; }
        if (newMes > 12) { newMes = 1; newAno++; }
        setViewMes(newMes);
        setViewAno(newAno);
    };

    return (
        <div className="bg-[var(--glass-bg)] rounded-xl p-3 border border-[var(--glass-border)]">
            <div className="flex items-center justify-between mb-3">
                <button type="button" onClick={() => handleMonthChange(-1)} className="p-1 hover:bg-[var(--glass-border)] rounded transition-colors text-[var(--text-primary)]">
                    <ChevronLeft />
                </button>
                <span className="text-[var(--text-primary)] text-sm font-bold capitalize">{nombreMes}</span>
                <button type="button" onClick={() => handleMonthChange(1)} className="p-1 hover:bg-[var(--glass-border)] rounded transition-colors text-[var(--text-primary)]">
                    <ChevronRight />
                </button>
            </div>

            <div className="grid grid-cols-7 gap-1 mb-1">
                {diasSemana.map(d => (
                    <div key={d} className="text-center text-[10px] text-[var(--text-muted)] font-black">{d}</div>
                ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
                {diasDelMes.map((dia, idx) => (
                    <button
                        key={idx}
                        type="button"
                        disabled={dia.empty || !dia.permitido}
                        onClick={() => dia.permitido && onSelect(dia.date)}
                        className={`aspect-square rounded-lg flex items-center justify-center text-xs transition-style
                            ${dia.empty ? 'invisible' : ''}
                            ${dia.selected ? 'bg-orange-500 text-white font-bold ring-2 ring-orange-400 ring-offset-1 ring-offset-[var(--bg-primary)]' : ''}
                            ${dia.permitido && !dia.selected ? 'bg-orange-500/20 text-orange-600 border border-orange-500/20 cursor-pointer font-bold' : ''}
                            ${!dia.permitido && !dia.empty ? 'bg-[var(--glass-bg)] text-[var(--text-muted)] opacity-20 cursor-not-allowed' : ''}
                        `}
                    >
                        {dia.day}
                    </button>
                ))}
            </div>
            {selectedDate && (
                <div className="mt-2 text-center text-[10px] font-black uppercase text-orange-500 tracking-widest">
                    Seleccionado: {formatDate(selectedDate)}
                </div>
            )}
        </div>
    );
}

// Multi-select Calendar Picker component for offering multiple days
function MultiCalendarPicker({ selectedDates = [], onToggle, diasPermitidos, colorAccent = 'purple' }) {
    const [viewMes, setViewMes] = useState(1);
    const [viewAno, setViewAno] = useState(2026);
    const diasSemana = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

    const diasPermitidosSet = useMemo(() => new Set(diasPermitidos || []), [diasPermitidos]);
    const selectedSet = useMemo(() => new Set(selectedDates), [selectedDates]);

    const diasDelMes = useMemo(() => {
        const firstDay = new Date(viewAno, viewMes - 1, 1);
        const lastDay = new Date(viewAno, viewMes, 0);
        const days = [];

        let startDayOfWeek = firstDay.getDay();
        startDayOfWeek = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1;

        for (let i = 0; i < startDayOfWeek; i++) {
            days.push({ empty: true });
        }

        for (let d = 1; d <= lastDay.getDate(); d++) {
            const dateStr = `${viewAno}-${String(viewMes).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
            days.push({
                day: d,
                date: dateStr,
                permitido: diasPermitidosSet.has(dateStr),
                selected: selectedSet.has(dateStr)
            });
        }
        return days;
    }, [viewAno, viewMes, diasPermitidosSet, selectedSet]);

    const nombreMes = new Date(viewAno, viewMes - 1).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });

    const handleMonthChange = (delta) => {
        let newMes = viewMes + delta;
        let newAno = viewAno;
        if (newMes < 1) { newMes = 12; newAno--; }
        if (newMes > 12) { newMes = 1; newAno++; }
        setViewMes(newMes);
        setViewAno(newAno);
    };

    const handleDayClick = (dateStr) => {
        onToggle(dateStr);
    };

    return (
        <div className="bg-[var(--glass-bg)] rounded-xl p-3 border border-[var(--glass-border)]">
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
                <button
                    type="button"
                    onClick={() => handleMonthChange(-1)}
                    className="p-1 hover:bg-[var(--glass-border)] rounded transition-colors text-[var(--text-primary)]"
                >
                    <ChevronLeft />
                </button>
                <span className="text-[var(--text-primary)] text-sm font-bold capitalize">{nombreMes}</span>
                <button
                    type="button"
                    onClick={() => handleMonthChange(1)}
                    className="p-1 hover:bg-[var(--glass-border)] rounded transition-colors text-[var(--text-primary)]"
                >
                    <ChevronRight />
                </button>
            </div>

            {/* Day headers */}
            <div className="grid grid-cols-7 gap-1 mb-1">
                {diasSemana.map(d => (
                    <div key={d} className="text-center text-[10px] text-[var(--text-muted)] font-black">
                        {d}
                    </div>
                ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1">
                {diasDelMes.map((dia, idx) => (
                    <button
                        key={idx}
                        type="button"
                        disabled={dia.empty || !dia.permitido}
                        onClick={() => dia.permitido && handleDayClick(dia.date)}
                        className={`aspect-square rounded-lg flex items-center justify-center text-xs transition-all
                            ${dia.empty ? 'invisible' : ''}
                            ${dia.selected ? (colorAccent === 'orange' ? 'bg-orange-500 shadow-orange-500/20' : 'bg-purple-500 shadow-purple-500/20') + ' text-white font-bold ring-2 ring-offset-1 ring-offset-[var(--bg-primary)] shadow-lg' : ''}
                            ${dia.permitido && !dia.selected ? (colorAccent === 'orange' ? 'bg-orange-500/20 text-orange-600 border-orange-500/30' : 'bg-purple-500/20 text-purple-600 border-purple-500/30') + ' cursor-pointer font-bold border' : ''}
                            ${!dia.permitido && !dia.empty ? 'bg-[var(--glass-bg)] text-[var(--text-muted)] opacity-20 cursor-not-allowed border border-transparent' : ''}
                        `}
                    >
                        {dia.day}
                    </button>
                ))}
            </div>

            {/* Selected dates display */}
            {
                selectedDates.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                        {selectedDates.map(date => (
                            <span
                                key={date}
                                className="bg-purple-500/20 text-purple-600 dark:text-purple-300 px-2 py-1 rounded-lg text-[10px] font-bold flex items-center gap-1 border border-purple-500/30"
                            >
                                {formatDateShort(date)}
                                <button
                                    type="button"
                                    onClick={() => onToggle(date)}
                                    className="hover:text-purple-800 dark:hover:text-white ml-1"
                                >
                                    ×
                                </button>
                            </span>
                        ))}
                    </div>
                )
            }
        </div >
    );
}

// Modal for creating new request
function ModalCrearSolicitud({ isOpen, onClose, onSubmit }) {
    const [diasNecesito, setDiasNecesito] = useState([]);
    const [diasOfrecidos, setDiasOfrecidos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [calendarData, setCalendarData] = useState({ diasTrabajo: [], diasLibres: [] });
    const [loadingCalendar, setLoadingCalendar] = useState(true);

    // Load calendar data when modal opens
    useEffect(() => {
        if (isOpen) {
            loadCalendarData();
        }
    }, [isOpen]);

    const loadCalendarData = async () => {
        setLoadingCalendar(true);
        try {
            const data = await cambios.getMiCalendario(2026);
            setCalendarData(data);
        } catch (error) {
            console.error('Error loading calendar:', error);
        } finally {
            setLoadingCalendar(false);
        }
    };

    const toggleDiaOfrecido = (dia) => {
        setDiasOfrecidos(prev =>
            prev.includes(dia)
                ? prev.filter(d => d !== dia)
                : [...prev, dia].sort()
        );
    };

    const toggleDiaNecesito = (dia) => {
        setDiasNecesito(prev =>
            prev.includes(dia)
                ? prev.filter(d => d !== dia)
                : [...prev, dia].sort()
        );
    };

    const handleSubmit = async () => {
        if (diasNecesito.length === 0) {
            alert('Selecciona al menos un día que necesitas librar');
            return;
        }
        if (diasOfrecidos.length < diasNecesito.length) {
            alert(`Debes ofrecer al menos ${diasNecesito.length} día(s) a cambio (uno por cada día que necesitas)`);
            return;
        }
        setLoading(true);
        try {
            // Create one request per needed day
            for (const diaNecesito of diasNecesito) {
                const ofertas = diasOfrecidos.map(dia => ({ dia, turno: 'M' }));
                await onSubmit({ diaNecesito, turnoNecesito: 'M', ofertas });
            }
            // Reset form
            setDiasNecesito([]);
            setDiasOfrecidos([]);
            onClose();
        } catch (error) {
            alert(error.message || 'Error al crear solicitud');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4 pb-24">
            <div className="bg-[var(--bg-secondary)] rounded-2xl p-5 w-full max-w-lg max-h-[85vh] overflow-y-auto border border-[var(--glass-border)] shadow-2xl">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-black text-[var(--text-primary)]">Nueva Solicitud</h2>
                    <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text-primary)] p-1">
                        <CloseIcon />
                    </button>
                </div>

                {loadingCalendar ? (
                    <div className="flex justify-center py-12">
                        <div className="w-8 h-8 border-4 border-orange-400 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                ) : (
                    <>
                        {/* Días que necesito librar - Multi-select */}
                        <div className="mb-5">
                            <label className="block text-orange-400 font-semibold mb-2">🎯 Días que NECESITO librar</label>
                            <p className="text-xs text-[var(--text-secondary)] mb-2">Solo puedes seleccionar días que trabajas</p>
                            <MultiCalendarPicker
                                selectedDates={diasNecesito}
                                onToggle={toggleDiaNecesito}
                                diasPermitidos={calendarData.diasTrabajo}
                                colorAccent="orange"
                            />
                        </div>

                        {/* Días que ofrezco - Multi-select */}
                        <div className="mb-5">
                            <label className="block text-purple-400 font-semibold mb-2">🔄 Días que OFREZCO a cambio</label>
                            <p className="text-xs text-[var(--text-secondary)] mb-2">Pulsa en los días libres que ofreces (puedes seleccionar varios)</p>
                            <MultiCalendarPicker
                                selectedDates={diasOfrecidos}
                                onToggle={toggleDiaOfrecido}
                                diasPermitidos={calendarData.diasLibres}
                            />
                        </div>

                        <button
                            onClick={handleSubmit}
                            disabled={loading || diasNecesito.length === 0 || diasOfrecidos.length < diasNecesito.length}
                            className="w-full bg-gradient-to-r from-orange-500 to-amber-500 text-white py-3 rounded-xl font-bold hover:opacity-90 transition-opacity disabled:opacity-50"
                        >
                            {loading ? 'Creando...' : 'Publicar Solicitud'}
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}

// Modal for responding to a request
function ModalResponder({ solicitud, isOpen, onClose, onSubmit }) {
    const [ofertaElegida, setOfertaElegida] = useState(null);
    const [loading, setLoading] = useState(false);
    const [misDiasLibres, setMisDiasLibres] = useState([]);
    const [diasComprometidos, setDiasComprometidos] = useState([]);
    const [loadingCalendar, setLoadingCalendar] = useState(true);

    // Load user's calendar to know which days they can work
    useEffect(() => {
        if (isOpen && solicitud) {
            loadMiCalendario();
            setOfertaElegida(null); // Reset selection when opening
        }
    }, [isOpen, solicitud]);

    const loadMiCalendario = async () => {
        setLoadingCalendar(true);
        try {
            const data = await cambios.getMiCalendario(2026);
            setMisDiasLibres(data.diasLibres || []);
            setDiasComprometidos(data.diasComprometidos || []);
        } catch (error) {
            console.error('Error loading calendar:', error);
            setMisDiasLibres([]);
            setDiasComprometidos([]);
        } finally {
            setLoadingCalendar(false);
        }
    };

    const handleSubmit = async () => {
        if (!ofertaElegida) {
            alert('Selecciona qué día te ofreces a trabajar');
            return;
        }
        setLoading(true);
        try {
            await onSubmit(solicitud.id, ofertaElegida);
            onClose();
        } catch (error) {
            alert(error.message || 'Error al responder');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen || !solicitud) return null;

    // Filter ofertas to only show days where the responder is WORKING
    // (not on vacation/libranza), because that's the day they'll receive as their day off
    // The responder will work on dia_necesito and get dia_ofrezco as their day off
    // Also exclude days already committed in other exchanges
    const misDiasLibresSet = new Set(misDiasLibres);
    const diasComprometidosSet = new Set(diasComprometidos);
    const ofertasCompatibles = (solicitud.ofertas || []).filter(o => {
        const fecha = o.dia_ofrezco?.split('T')[0];
        // Only show days where:
        // 1. The responder currently WORKS (NOT libre)
        // 2. The day is NOT already committed in another exchange
        return !misDiasLibresSet.has(fecha) && !diasComprometidosSet.has(fecha);
    });

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-[var(--bg-secondary)] rounded-2xl p-6 w-full max-w-md border border-[var(--glass-border)] shadow-2xl">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-black text-[var(--text-primary)]">Responder</h2>
                    <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text-primary)] p-1">
                        <CloseIcon />
                    </button>
                </div>

                <div className="mb-4">
                    <p className="text-[var(--text-muted)] mb-2 font-medium">
                        <span className="text-orange-500 font-bold">{solicitud.conductor_nombre}</span> necesita el{' '}
                        <span className="text-[var(--text-primary)] font-bold">{formatDate(solicitud.dia_necesito?.split('T')[0])}</span>
                    </p>
                    <p className="text-[var(--text-muted)] text-xs font-medium">
                        Tú trabajarás ese día por él/ella. Selecciona qué día te dará libre a cambio:
                    </p>
                </div>

                {loadingCalendar ? (
                    <div className="flex justify-center py-8">
                        <div className="w-6 h-6 border-2 border-orange-400 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                ) : ofertasCompatibles.length === 0 ? (
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-6">
                        <p className="text-amber-400 text-sm">
                            ⚠️ No puedes aceptar esta solicitud porque tienes libranza o vacaciones en todos los días ofrecidos.
                        </p>
                        <p className="text-[var(--text-muted)] text-xs mt-2">
                            Días ofrecidos: {(solicitud.ofertas || []).map(o => formatDateShort(o.dia_ofrezco?.split('T')[0])).join(', ')}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-2 mb-6">
                        {ofertasCompatibles.map((o) => (
                            <button
                                key={o.id}
                                onClick={() => setOfertaElegida(o.id)}
                                className={`w-full p-3 rounded-lg text-left transition-all ${ofertaElegida === o.id
                                    ? 'bg-purple-500/20 border-2 border-purple-500 shadow-lg shadow-purple-500/10'
                                    : 'bg-[var(--glass-bg)] border border-[var(--glass-border)] hover:border-purple-500/50'
                                    }`}
                            >
                                <span className="text-[var(--text-primary)] font-bold">{formatDate(o.dia_ofrezco?.split('T')[0])}</span>
                                <span className="text-[var(--text-muted)] ml-2 text-xs font-bold uppercase tracking-widest">({o.turno_ofrezco})</span>
                            </button>
                        ))}
                    </div>
                )}

                <button
                    onClick={handleSubmit}
                    disabled={loading || !ofertaElegida || ofertasCompatibles.length === 0}
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 rounded-xl font-bold disabled:opacity-50 transition-colors"
                >
                    {loading ? 'Enviando...' : 'Enviar Respuesta'}
                </button>
            </div>
        </div>
    );
}

// Modal for viewing responses to my request
function ModalVerRespuestas({ solicitud, isOpen, onClose, onAceptar }) {
    const [respuestas, setRespuestas] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen && solicitud) {
            loadRespuestas();
        }
    }, [isOpen, solicitud]);

    const loadRespuestas = async () => {
        try {
            const data = await cambios.getDetalle(solicitud.id);
            setRespuestas(data.respuestas || []);
        } catch (error) {
            console.error('Error loading respuestas:', error);
        }
    };

    const handleAceptar = async (respuestaId) => {
        setLoading(true);
        try {
            await onAceptar(solicitud.id, respuestaId);
            onClose();
        } catch (error) {
            console.error(error);
            // alert(error.message || 'Error al aceptar'); // TODO: Use toast notification instead
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen || !solicitud) return null;

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-[var(--bg-secondary)] rounded-2xl p-6 w-full max-w-md max-h-[80vh] overflow-y-auto border border-[var(--glass-border)] shadow-2xl">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-black text-[var(--text-primary)]">Respuestas</h2>
                    <button onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text-primary)] p-1">
                        <CloseIcon />
                    </button>
                </div>

                <div className="mb-4">
                    <p className="text-[var(--text-muted)] text-[10px] uppercase font-black tracking-widest">
                        Tu solicitud para el <span className="text-orange-500">{formatDate(solicitud.dia_necesito?.split('T')[0])}</span>
                    </p>
                </div>

                {respuestas.length === 0 ? (
                    <p className="text-[var(--text-secondary)] text-center py-8">No hay respuestas aún</p>
                ) : (
                    <div className="space-y-3">
                        {respuestas.filter(r => r.estado === 'pendiente').map((r) => (
                            <div key={r.id} className="bg-[var(--glass-bg)] rounded-xl p-4 border border-[var(--glass-border)]">
                                <p className="text-[var(--text-primary)] font-bold mb-1">{r.conductor_nombre}</p>
                                <p className="text-[var(--text-muted)] text-[10px] font-black uppercase tracking-widest mb-3">
                                    Quiere el <span className="text-purple-500">{formatDate(r.dia_ofrezco?.split('T')[0])}</span>
                                </p>
                                <button
                                    onClick={() => handleAceptar(r.id)}
                                    disabled={loading}
                                    className="w-full bg-orange-500 hover:bg-orange-600 text-white py-2 rounded-lg text-sm font-bold shadow-lg shadow-orange-500/20 disabled:opacity-50 transition-all active:scale-95"
                                >
                                    Aceptar intercambio
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

// Main page component
export default function CambiosDias() {
    const [vista, setVista] = useState('calendario'); // 'calendario' or 'lista'
    const [filtro, setFiltro] = useState('paraMi'); // 'paraMi' or 'todas'
    const [ano, setAno] = useState(2026);
    const [mes, setMes] = useState(1);

    const [calendarioData, setCalendarioData] = useState({ diasOfrecidos: [], misDiasLibres: [] });
    const [misSolicitudes, setMisSolicitudes] = useState([]);
    const [solicitudes, setSolicitudes] = useState([]);
    const [loading, setLoading] = useState(true);

    const [modalCrear, setModalCrear] = useState(false);
    const [modalResponder, setModalResponder] = useState(null);
    const [modalRespuestas, setModalRespuestas] = useState(null);
    const [modalCancelar, setModalCancelar] = useState(null); // ID de solicitud a cancelar

    // Onboarding tour state
    const [showTour, setShowTour] = useState(false);
    const [tourCurrentStep, setTourCurrentStep] = useState(0);

    // Show tour on first visit
    useEffect(() => {
        const hasSeenTour = localStorage.getItem('onboarding_mercadillo_seen');
        if (!hasSeenTour) {
            const timer = setTimeout(() => setShowTour(true), 1000);
            return () => clearTimeout(timer);
        }
    }, []);

    const handleTourComplete = () => {
        setShowTour(false);
        localStorage.setItem('onboarding_mercadillo_seen', 'true');
    };

    const handleTourSkip = () => {
        setShowTour(false);
        localStorage.setItem('onboarding_mercadillo_seen', 'true');
    };

    const restartTour = () => {
        setTourCurrentStep(0);
        setShowTour(true);
    };

    const handleTourStepChange = (stepIndex) => {
        setTourCurrentStep(stepIndex);
    };

    // Simulated day for tour step 5 (index 4) - ONLY for visual demo, NOT saved to DB
    const diasOfrecidosConSimulacion = useMemo(() => {
        // Step 5 (index 4) is "Días con ofertas" - show a fake orange day
        if (showTour && tourCurrentStep === 4) {
            const diaFake = {
                fecha: `${ano}-${String(mes).padStart(2, '0')}-15`, // Day 15 of current month
                numOfertas: 1
                // No ID, no real data - purely visual
            };
            return [...(calendarioData.diasOfrecidos || []), diaFake];
        }
        return calendarioData.diasOfrecidos;
    }, [calendarioData.diasOfrecidos, showTour, tourCurrentStep, ano, mes]);

    useEffect(() => {
        loadData();
    }, [ano, mes, filtro]);

    const loadData = async () => {
        setLoading(true);
        try {
            // Load calendar data - pass filter so backend filters compatible days
            const calData = await cambios.getCalendario(ano, mes, filtro);
            setCalendarioData(calData);

            // Load my requests
            const mis = await cambios.getMisSolicitudes();
            setMisSolicitudes(mis);

            // Load other requests
            const otras = filtro === 'paraMi'
                ? await cambios.getCompatibles()
                : await cambios.getTodas();
            setSolicitudes(otras);
        } catch (error) {
            console.error('Error loading cambios data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleMonthChange = (delta) => {
        let newMes = mes + delta;
        let newAno = ano;
        if (newMes < 1) { newMes = 12; newAno--; }
        if (newMes > 12) { newMes = 1; newAno++; }
        setMes(newMes);
        setAno(newAno);
    };

    const handleDayClick = (dia) => {
        if (dia.oferta) {
            // Show offers for this day - for now just switch to list view
            setVista('lista');
        }
    };

    const handleCrearSolicitud = async (data) => {
        await cambios.crear(data);
        loadData();
    };

    const handleResponder = async (solicitudId, ofertaElegidaId) => {
        await cambios.responder(solicitudId, ofertaElegidaId);
        loadData();
    };

    const handleAceptar = async (solicitudId, respuestaId) => {
        await cambios.aceptar(solicitudId, respuestaId);
        loadData();
    };

    const handleCancelar = (solicitudId) => {
        setModalCancelar(solicitudId);
    };

    const confirmCancelar = async () => {
        if (!modalCancelar) return;
        try {
            await cambios.cancelar(modalCancelar);
            setModalCancelar(null);
            loadData();
        } catch (error) {
            alert(error.message || 'Error al cancelar');
        }
    };

    return (
        <div className="min-h-screen pb-20" style={{ background: 'var(--bg-primary)' }}>
            {/* Header */}
            <div id="tour-mercadillo-header" className="bg-gradient-to-r from-orange-600 to-amber-600 px-4 py-6 rounded-b-3xl shadow-lg relative">
                <div className="flex items-center justify-between mb-4">
                    <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                        <SwapIcon /> Intercambio de Días
                    </h1>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={restartTour}
                            className="bg-white/20 hover:bg-white/30 backdrop-blur p-2 rounded-full transition-colors"
                            title="Ver ayuda"
                        >
                            <HelpCircle className="w-5 h-5" />
                        </button>
                        <button
                            id="tour-mercadillo-create"
                            onClick={() => setModalCrear(true)}
                            className="bg-white/20 hover:bg-white/30 backdrop-blur p-3 rounded-full transition-colors"
                        >
                            <PlusIcon />
                        </button>
                    </div>
                </div>

                {/* View toggle */}
                <div id="tour-mercadillo-view-toggle" className="flex gap-2">
                    <button
                        onClick={() => setVista('calendario')}
                        className={`flex-1 py-2.5 rounded-xl flex items-center justify-center gap-2 transition-colors font-bold ${vista === 'calendario' ? 'bg-white text-orange-600 shadow-md' : 'bg-white/20 text-white hover:bg-white/30'
                            }`}
                    >
                        <CalendarIcon /> Calendario
                    </button>
                    <button
                        onClick={() => setVista('lista')}
                        className={`flex-1 py-2.5 rounded-xl flex items-center justify-center gap-2 transition-colors font-bold ${vista === 'lista' ? 'bg-white text-orange-600 shadow-md' : 'bg-white/20 text-white hover:bg-white/30'
                            }`}
                    >
                        <ListIcon /> Lista
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="p-4">
                {/* Filter tabs */}
                <div id="tour-mercadillo-filter" className="flex gap-2 mb-4">
                    <button
                        onClick={() => setFiltro('paraMi')}
                        className={`px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest transition-all ${filtro === 'paraMi' ? 'bg-orange-600 text-white shadow-lg shadow-orange-500/30' : 'bg-[var(--glass-bg)] text-slate-700 dark:text-slate-300 border border-[var(--glass-border)] hover:bg-slate-200 dark:hover:bg-slate-800'
                            }`}
                    >
                        Para mí
                    </button>
                    <button
                        onClick={() => setFiltro('todas')}
                        className={`px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest transition-all ${filtro === 'todas' ? 'bg-orange-600 text-white shadow-lg shadow-orange-500/30' : 'bg-[var(--glass-bg)] text-slate-700 dark:text-slate-300 border border-[var(--glass-border)] hover:bg-slate-200 dark:hover:bg-slate-800'
                            }`}
                    >
                        Todas
                    </button>
                </div>

                {loading ? (
                    <div className="flex justify-center py-12">
                        <div className="w-8 h-8 border-4 border-orange-400 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                ) : (
                    <>
                        {/* Calendar View */}
                        {vista === 'calendario' && (
                            <div id="tour-mercadillo-calendar">
                                <CalendarioCambios
                                    ano={ano}
                                    mes={mes}
                                    diasOfrecidos={diasOfrecidosConSimulacion}
                                    misDiasLibres={calendarioData.misDiasLibres}
                                    onDayClick={handleDayClick}
                                    onMonthChange={handleMonthChange}
                                />
                            </div>
                        )}

                        {/* List View */}
                        {vista === 'lista' && (
                            <div className="space-y-4">
                                {/* My requests first */}
                                {misSolicitudes.filter(s => s.estado === 'abierta').length > 0 && (
                                    <div className="mb-6">
                                        <h3 className="text-cyan-400 font-semibold mb-3">Mis solicitudes</h3>
                                        <div className="space-y-3">
                                            {misSolicitudes.filter(s => s.estado === 'abierta').map(s => (
                                                <SolicitudCard
                                                    key={s.id}
                                                    solicitud={s}
                                                    esMia={true}
                                                    onVerRespuestas={() => setModalRespuestas(s)}
                                                    onCancelar={handleCancelar}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Other requests */}
                                <div>
                                    <h3 className="text-orange-400 font-semibold mb-3">
                                        {filtro === 'paraMi' ? 'Solicitudes compatibles' : 'Todas las solicitudes'}
                                    </h3>
                                    {solicitudes.length === 0 ? (
                                        <div className="text-center py-12 text-gray-500">
                                            <p>No hay solicitudes {filtro === 'paraMi' ? 'compatibles contigo' : 'activas'}</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            {solicitudes.map(s => (
                                                <SolicitudCard
                                                    key={s.id}
                                                    solicitud={s}
                                                    esMia={false}
                                                    onResponder={() => setModalResponder(s)}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Modals */}
            <ModalCrearSolicitud
                isOpen={modalCrear}
                onClose={() => setModalCrear(false)}
                onSubmit={handleCrearSolicitud}
            />
            <ModalResponder
                solicitud={modalResponder}
                isOpen={!!modalResponder}
                onClose={() => setModalResponder(null)}
                onSubmit={handleResponder}
            />
            <ModalVerRespuestas
                solicitud={modalRespuestas}
                isOpen={!!modalRespuestas}
                onClose={() => setModalRespuestas(null)}
                onAceptar={handleAceptar}
            />

            {/* Modal de confirmación para cancelar */}
            {modalCancelar && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-[var(--bg-secondary)] rounded-2xl p-6 w-full max-w-sm border border-[var(--glass-border)] shadow-2xl">
                        <h3 className="text-xl font-black text-[var(--text-primary)] mb-4">¿Cancelar solicitud?</h3>
                        <p className="text-[var(--text-muted)] mb-6 text-sm font-medium">Esta acción no se puede deshacer. La solicitud se eliminará y los compañeros que hayan respondido serán notificados.</p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setModalCancelar(null)}
                                className="flex-1 bg-[var(--glass-bg)] hover:bg-[var(--glass-border)] text-[var(--text-primary)] py-3 rounded-xl font-bold transition-all"
                            >
                                No, mantener
                            </button>
                            <button
                                onClick={confirmCancelar}
                                className="flex-1 bg-red-500 hover:bg-red-600 text-white py-3 rounded-xl font-bold transition-all shadow-lg shadow-red-500/20"
                            >
                                Sí, cancelar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Onboarding Tour */}
            <OnboardingTour
                steps={MERCADILLO_TOUR_STEPS}
                onComplete={handleTourComplete}
                onSkip={handleTourSkip}
                isOpen={showTour}
                onStepChange={handleTourStepChange}
            />
        </div>
    );
}
